/**
 * /admin/users/$userId - the member record page.
 *
 * A real route, not a drawer (C18). It sits OUTSIDE the /admin/users roster
 * route (the trailing `users_` segment), so opening a record replaces the list
 * rather than nesting under it.
 *
 * ── The `$userId` key scheme ────────────────────────────────────────────────
 * Two kinds of row can open a record, so the segment carries both:
 *
 *   /admin/users/9f3c1a80-8c2f-4e17-9d31-1f2a3b4c5471   a real member (uuid)
 *   /admin/users/pending_4b81ff02-…                      a paid, not-onboarded
 *                                                        `allowed_signups` row
 *
 * The roster's own selection key is `pending:<id>` and the edge function's
 * `get` action takes that form verbatim, but a colon in a PATH segment gets
 * percent-encoded by routers and mangled by copy-paste. The underscore spelling
 * is URL-safe and round-trips through `recordKeyToApiUserId` in
 * api/admin-users.ts, which is the only place the two spellings meet.
 *
 * ── The `from` search param ─────────────────────────────────────────────────
 * The roster keeps its filters, sort and page in ITS url (invariant I3). A row
 * click passes that querystring through as `?from=…` so "Back to Users" returns
 * the list exactly as it was. It is optional: a bookmarked or hand-typed record
 * URL simply falls back to a bare /admin/users. (The roster starts sending it
 * when the roster phase lands; nothing here depends on that.)
 */
import { createFileRoute } from '@tanstack/react-router';
import { MemberRecordPage } from '@/components/admin/users/record/MemberRecordPage';

export interface MemberRecordSearch {
  /** The roster's own search string, url-encoded. Optional. */
  from?: string;
}

export const Route = createFileRoute('/admin/users_/$userId')({
  component: MemberRecordRoute,
  validateSearch: (search: Record<string, unknown>): MemberRecordSearch => {
    const from = typeof search.from === 'string' && search.from.trim() !== ''
      ? search.from
      : undefined;
    return from ? { from } : {};
  },
});

function MemberRecordRoute() {
  const { userId } = Route.useParams();
  const { from } = Route.useSearch();
  return <MemberRecordPage recordKey={userId} backSearch={from ?? null} />;
}
